import React, { useState, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import {
    Box,
    Button,
    CircularProgress,
    makeStyles,
    Paper,
    Theme,
    Typography,
} from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ErrorIcon from '@material-ui/icons/Error'
import WarningIcon from '@material-ui/icons/Warning'
import DescriptionIcon from '@material-ui/icons/Description'
import AccessTimeIcon from '@material-ui/icons/AccessTime'
import BusinessIcon from '@material-ui/icons/Business'
import {
    useVendorCOIUpload,
    NotificationType,
} from '../../hooks/useVendorCOIUpload'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    container: {
        maxWidth: 600,
        width: '100%',
    },
    header: {
        textAlign: 'center',
        marginBottom: theme.spacing(4),
    },
    logo: {
        width: 180,
        marginBottom: theme.spacing(2),
    },
    title: {
        fontWeight: 700,
        color: '#1e293b',
        marginBottom: theme.spacing(1),
    },
    subtitle: {
        color: '#64748b',
        fontSize: 16,
    },
    card: {
        borderRadius: 16,
        padding: theme.spacing(4),
        boxShadow:
            '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    },
    infoSection: {
        backgroundColor: '#f1f5f9',
        borderRadius: 12,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(3),
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        '&:last-child': {
            marginBottom: 0,
        },
    },
    infoIcon: {
        fontSize: 20,
        color: '#64748b',
    },
    infoLabel: {
        color: '#64748b',
        fontSize: 14,
        minWidth: 100,
    },
    infoValue: {
        fontWeight: 600,
        color: '#1e293b',
        fontSize: 14,
    },
    alertBox: {
        borderRadius: 12,
        padding: theme.spacing(2),
        marginBottom: theme.spacing(3),
        display: 'flex',
        gap: theme.spacing(1.5),
    },
    alertError: {
        backgroundColor: '#fef2f2',
        border: '1px solid #fecaca',
    },
    alertWarning: {
        backgroundColor: '#fffbeb',
        border: '1px solid #fde68a',
    },
    alertIcon: {
        fontSize: 22,
        flexShrink: 0,
        marginTop: 2,
    },
    alertIconError: {
        color: '#dc2626',
    },
    alertIconWarning: {
        color: '#d97706',
    },
    alertContent: {
        flex: 1,
    },
    alertTitle: {
        fontWeight: 600,
        fontSize: 14,
        marginBottom: theme.spacing(0.5),
    },
    alertTitleError: {
        color: '#991b1b',
    },
    alertTitleWarning: {
        color: '#92400e',
    },
    issuesList: {
        margin: 0,
        paddingLeft: theme.spacing(2),
        fontSize: 13,
    },
    issueItem: {
        marginBottom: theme.spacing(0.5),
        color: '#6b7280',
    },
    dropzone: {
        border: '2px dashed #cbd5e1',
        borderRadius: 12,
        padding: theme.spacing(4),
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: '#fafafa',
        '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: '#f0f9ff',
        },
    },
    dropzoneActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: '#f0f9ff',
    },
    dropzoneDisabled: {
        cursor: 'not-allowed',
        opacity: 0.6,
        '&:hover': {
            borderColor: '#cbd5e1',
            backgroundColor: '#fafafa',
        },
    },
    uploadIcon: {
        fontSize: 48,
        color: '#94a3b8',
        marginBottom: theme.spacing(2),
    },
    dropzoneText: {
        color: '#64748b',
        marginBottom: theme.spacing(1),
    },
    dropzoneHint: {
        color: '#94a3b8',
        fontSize: 13,
    },
    selectedFile: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        backgroundColor: '#f1f5f9',
        borderRadius: 8,
        padding: theme.spacing(1.5, 2),
        marginTop: theme.spacing(2),
    },
    fileIcon: {
        fontSize: 24,
        color: theme.palette.primary.main,
    },
    fileName: {
        flex: 1,
        fontWeight: 500,
        fontSize: 14,
        wordBreak: 'break-all',
    },
    fileSize: {
        color: '#64748b',
        fontSize: 13,
    },
    removeFile: {
        color: '#ef4444',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 500,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    submitButton: {
        marginTop: theme.spacing(3),
        padding: theme.spacing(1.5),
        borderRadius: 10,
        fontWeight: 600,
        fontSize: 16,
    },
    successContainer: {
        textAlign: 'center',
        padding: theme.spacing(4),
    },
    successIcon: {
        fontSize: 64,
        color: '#22c55e',
        marginBottom: theme.spacing(2),
    },
    successTitle: {
        fontWeight: 700,
        color: '#166534',
        marginBottom: theme.spacing(1),
    },
    successMessage: {
        color: '#64748b',
        fontSize: 16,
    },
    errorContainer: {
        textAlign: 'center',
        padding: theme.spacing(4),
    },
    errorIcon: {
        fontSize: 64,
        color: '#ef4444',
        marginBottom: theme.spacing(2),
    },
    errorTitle: {
        fontWeight: 700,
        color: '#991b1b',
        marginBottom: theme.spacing(1),
    },
    errorMessage: {
        color: '#64748b',
        fontSize: 16,
    },
    expiryWarning: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(3),
        color: '#64748b',
        fontSize: 13,
    },
    expiryIcon: {
        fontSize: 16,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 300,
        gap: theme.spacing(2),
    },
}))

const getNotificationInfo = (type: NotificationType) => {
    switch (type) {
        case 'NON_COMPLIANT':
            return {
                title:
                    'Your Certificate of Insurance does not meet requirements',
                isError: true,
            }
        case 'EXPIRED':
            return {
                title: 'Your Certificate of Insurance has expired',
                isError: true,
            }
        case 'EXPIRING_SOON':
            return {
                title: 'Your Certificate of Insurance is expiring soon',
                isError: false,
            }
        case 'MISSING_COI':
            return {
                title: 'A Certificate of Insurance is required',
                isError: true,
            }
        default:
            return {
                title: 'Please upload a valid Certificate of Insurance',
                isError: false,
            }
    }
}

const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const formatExpiryDate = (dateString: string): string => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

export const VendorCOIUploadPage: React.FC = () => {
    const classes = useStyles()
    const location = useLocation()
    const [selectedFile, setSelectedFile] = useState<File | null>(null)
    const [isDragActive, setIsDragActive] = useState(false)
    const [uploadSuccess, setUploadSuccess] = useState(false)

    // Extract token from URL
    const token = useMemo(() => {
        const params = new URLSearchParams(location.search)
        return params.get('token')
    }, [location.search])

    const {
        uploadInfo,
        isLoadingInfo,
        errorInfo,
        uploadCOI,
        isUploading,
    } = useVendorCOIUpload(token)

    const handleDragEnter = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(true)
    }, [])

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)
    }, [])

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }, [])

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)

        const files = e.dataTransfer.files
        if (files.length > 0) {
            const file = files[0]
            if (file.type === 'application/pdf') {
                setSelectedFile(file)
            }
        }
    }, [])

    const handleFileSelect = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = e.target.files
            if (files && files.length > 0) {
                setSelectedFile(files[0])
            }
        },
        [],
    )

    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null)
    }, [])

    const handleSubmit = useCallback(async () => {
        if (!selectedFile) return

        try {
            await uploadCOI.mutateAsync({ file: selectedFile })
            setUploadSuccess(true)
        } catch {
            // Error handled in mutation
        }
    }, [selectedFile, uploadCOI])

    // No token provided
    if (!token) {
        return (
            <Box className={classes.root}>
                <Box className={classes.container}>
                    <Paper className={classes.card}>
                        <Box className={classes.errorContainer}>
                            <ErrorIcon className={classes.errorIcon} />
                            <Typography
                                variant="h5"
                                className={classes.errorTitle}
                            >
                                Invalid Link
                            </Typography>
                            <Typography className={classes.errorMessage}>
                                This upload link is invalid or incomplete.
                                Please use the link provided in your email.
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        )
    }

    // Loading state
    if (isLoadingInfo) {
        return (
            <Box className={classes.root}>
                <Box className={classes.container}>
                    <Paper className={classes.card}>
                        <Box className={classes.loadingContainer}>
                            <CircularProgress size={40} />
                            <Typography color="textSecondary">
                                Loading upload page...
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        )
    }

    // Error loading info (expired, invalid token, etc.)
    if (errorInfo || !uploadInfo) {
        const errorMessage =
            (errorInfo as any)?.response?.data?.error ||
            (errorInfo as any)?.response?.data?.message ||
            'This upload link has expired or is no longer valid.'
        return (
            <Box className={classes.root}>
                <Box className={classes.container}>
                    <Paper className={classes.card}>
                        <Box className={classes.errorContainer}>
                            <ErrorIcon className={classes.errorIcon} />
                            <Typography
                                variant="h5"
                                className={classes.errorTitle}
                            >
                                Link Expired
                            </Typography>
                            <Typography className={classes.errorMessage}>
                                {errorMessage}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        )
    }

    // Already uploaded
    if (uploadInfo.already_uploaded) {
        return (
            <Box className={classes.root}>
                <Box className={classes.container}>
                    <Paper className={classes.card}>
                        <Box className={classes.successContainer}>
                            <CheckCircleIcon className={classes.successIcon} />
                            <Typography
                                variant="h5"
                                className={classes.successTitle}
                            >
                                Already Uploaded
                            </Typography>
                            <Typography className={classes.successMessage}>
                                A certificate has already been uploaded using
                                this link. If you need to upload a different
                                document, please contact the property manager.
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        )
    }

    // Upload success
    if (uploadSuccess) {
        return (
            <Box className={classes.root}>
                <Box className={classes.container}>
                    <Paper className={classes.card}>
                        <Box className={classes.successContainer}>
                            <CheckCircleIcon className={classes.successIcon} />
                            <Typography
                                variant="h5"
                                className={classes.successTitle}
                            >
                                Upload Successful!
                            </Typography>
                            <Typography className={classes.successMessage}>
                                {uploadInfo.apartment_names &&
                                uploadInfo.apartment_names.length > 1
                                    ? 'Your Certificate of Insurance was filed with each property on this request. Each property will review it separately.'
                                    : 'Your Certificate of Insurance has been submitted successfully. The property manager will review it and update your compliance status.'}
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            </Box>
        )
    }

    const notificationInfo = getNotificationInfo(uploadInfo.notification_type)

    return (
        <Box className={classes.root}>
            <Box className={classes.container}>
                {/* Header */}
                <Box className={classes.header}>
                    <Typography variant="h4" className={classes.title}>
                        Upload Certificate of Insurance
                    </Typography>
                    <Typography className={classes.subtitle}>
                        Please upload a valid Certificate of Insurance (COI)
                        document
                    </Typography>
                </Box>

                <Paper className={classes.card}>
                    {/* Property & Vendor Info */}
                    <Box className={classes.infoSection}>
                        <Box className={classes.infoRow}>
                            <BusinessIcon className={classes.infoIcon} />
                            <Typography className={classes.infoLabel}>
                                {uploadInfo.apartment_names &&
                                uploadInfo.apartment_names.length > 1
                                    ? 'Properties:'
                                    : 'Property:'}
                            </Typography>
                            <Typography className={classes.infoValue}>
                                {uploadInfo.apartment_names &&
                                uploadInfo.apartment_names.length > 1
                                    ? uploadInfo.apartment_names.join(', ')
                                    : uploadInfo.apartment_name}
                            </Typography>
                        </Box>
                        <Box className={classes.infoRow}>
                            <DescriptionIcon className={classes.infoIcon} />
                            <Typography className={classes.infoLabel}>
                                Vendor:
                            </Typography>
                            <Typography className={classes.infoValue}>
                                {uploadInfo.vendor_name}
                            </Typography>
                        </Box>
                    </Box>
                    {uploadInfo.apartment_names &&
                        uploadInfo.apartment_names.length > 1 && (
                            <Typography
                                className={classes.subtitle}
                                style={{ marginBottom: 16 }}
                            >
                                This file will be filed with each property
                                listed above. Each property reviews it
                                separately.
                            </Typography>
                        )}

                    {/* Compliance Issues Alert */}
                    <Box
                        className={`${classes.alertBox} ${
                            notificationInfo.isError
                                ? classes.alertError
                                : classes.alertWarning
                        }`}
                    >
                        {notificationInfo.isError ? (
                            <ErrorIcon
                                className={`${classes.alertIcon} ${classes.alertIconError}`}
                            />
                        ) : (
                            <WarningIcon
                                className={`${classes.alertIcon} ${classes.alertIconWarning}`}
                            />
                        )}
                        <Box className={classes.alertContent}>
                            <Typography
                                className={`${classes.alertTitle} ${
                                    notificationInfo.isError
                                        ? classes.alertTitleError
                                        : classes.alertTitleWarning
                                }`}
                            >
                                {notificationInfo.title}
                            </Typography>
                            {uploadInfo.compliance_issues.length > 0 && (
                                <ul className={classes.issuesList}>
                                    {uploadInfo.compliance_issues.map(
                                        (issue, index) => (
                                            <li
                                                key={index}
                                                className={classes.issueItem}
                                            >
                                                {issue}
                                            </li>
                                        ),
                                    )}
                                </ul>
                            )}
                        </Box>
                    </Box>

                    {/* Dropzone */}
                    <input
                        type="file"
                        id="coi-file-input"
                        accept=".pdf"
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                    <label htmlFor="coi-file-input">
                        <Box
                            className={`${classes.dropzone} ${
                                isDragActive ? classes.dropzoneActive : ''
                            } ${isUploading ? classes.dropzoneDisabled : ''}`}
                            onDragEnter={handleDragEnter}
                            onDragLeave={handleDragLeave}
                            onDragOver={handleDragOver}
                            onDrop={handleDrop}
                        >
                            <CloudUploadIcon className={classes.uploadIcon} />
                            <Typography className={classes.dropzoneText}>
                                Drag and drop your PDF here, or click to browse
                            </Typography>
                            <Typography className={classes.dropzoneHint}>
                                PDF files only, max 10MB
                            </Typography>
                        </Box>
                    </label>

                    {/* Selected File */}
                    {selectedFile && (
                        <Box className={classes.selectedFile}>
                            <DescriptionIcon className={classes.fileIcon} />
                            <Typography className={classes.fileName}>
                                {selectedFile.name}
                            </Typography>
                            <Typography className={classes.fileSize}>
                                {formatFileSize(selectedFile.size)}
                            </Typography>
                            <Typography
                                className={classes.removeFile}
                                onClick={handleRemoveFile}
                            >
                                Remove
                            </Typography>
                        </Box>
                    )}

                    {/* Submit Button */}
                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="large"
                        className={classes.submitButton}
                        onClick={handleSubmit}
                        disabled={!selectedFile || isUploading}
                        startIcon={
                            isUploading ? (
                                <CircularProgress size={20} color="inherit" />
                            ) : (
                                <CloudUploadIcon />
                            )
                        }
                    >
                        {isUploading ? 'Uploading...' : 'Upload Certificate'}
                    </Button>

                    {/* Expiry Warning */}
                    <Box className={classes.expiryWarning}>
                        <AccessTimeIcon className={classes.expiryIcon} />
                        <Typography variant="caption">
                            This link expires on{' '}
                            {formatExpiryDate(uploadInfo.expires_at)}
                        </Typography>
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}

export default VendorCOIUploadPage
